import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { AdminIdleGuard, LogoutButton, ProfileMenu } from "@/components/ProfileMenu";
import {
  LayoutDashboard, Library, BookOpenCheck, UserRound, ShieldCheck, Building2, Users, ScrollText,
  Settings, GraduationCap, Menu, BadgeCheck, KeyRound, Award, Network, Wallet, Globe, Megaphone, LifeBuoy,
  Lock, BarChart3, Handshake, Plug, Sparkles, Info,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "@/components/Logo";
import { useMe } from "@/context/MeContext";
import { useApi } from "@/lib/api";

const MODS = [
  ["assessments", "Assessments & Credentials", Award, ["assess.manage"]],
  ["organisations", "Organisations & Groups", Network, ["orgs.manage", "org.self"]],
  ["sales", "Pricing, Sales & Payments", Wallet, ["sales.view", "sales.manage"]],
  ["website", "Website & Content", Globe, ["website.edit", "website.publish"]],
  ["comms", "Communications", Megaphone, ["comms.manage"]],
  ["support", "Support & Moderation", LifeBuoy, ["support.manage"]],
  ["privacy", "Security & Compliance", Lock, ["privacy.manage"]],
  ["analytics", "Analytics & Reports", BarChart3, ["analytics.view"]],
  ["partners", "Instructors & Partners", Handshake, ["partners.manage"]],
  ["operations", "Integrations & Operations", Plug, ["ops.view"]],
  ["ai", "AI & Recommendations", Sparkles, ["ai.manage"]],
].map(([k, label, icon, perm]) => ({ to: `/admin/m/${k}`, label, icon, perm }));

const NAV = {
  learner: [
    { to: "/learn", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/learn/courses", label: "Course library", icon: Library },
    { to: "/learn/my-courses", label: "My courses", icon: BookOpenCheck },
    { to: "/learn/assessments", label: "Assessments", icon: Award },
    { to: "/learn/organisation", label: "My organisation", icon: Network },
    { to: "/learn/profile", label: "Profile", icon: UserRound },
    { to: "/account", label: "Privacy & data", icon: ShieldCheck },
  ],
  employer: [
    { to: "/employer", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/employer/profile", label: "Company profile", icon: Building2 },
    { to: "/account", label: "Privacy & data", icon: ShieldCheck },
  ],
  admin: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/users", label: "Users", icon: Users, perm: "users.view" },
    { to: "/admin/roles", label: "Roles & permissions", icon: KeyRound, perm: "roles" },
    { to: "/admin/courses", label: "Courses", icon: GraduationCap, perm: ["courses.view", "courses.edit"] },
    { to: "/admin/employers", label: "Employer approvals", icon: BadgeCheck, perm: "employers.approve" },
    { to: "/admin/audit", label: "Audit log", icon: ScrollText, perm: "audit.view" },
    { to: "/admin/settings", label: "Settings", icon: Settings, perm: ["settings.view", "settings.edit"] },
    ...MODS,
    { to: "/account", label: "Privacy & data", icon: ShieldCheck },
  ],
};

function allowed(item, user) {
  if (!item.perm) return true;
  if (item.perm === "roles") return user.role === "super_admin";
  return [].concat(item.perm).some((p) => user.permissions.includes(p));
}

const CRUMBS = { admin: "Admin", users: "Users", roles: "Roles & permissions", courses: "Courses", employers: "Employer approvals", audit: "Audit log", settings: "Settings",
  m: "Modules", ...Object.fromEntries(MODS.map((x) => [x.to.split("/").pop(), x.label])) };

function Messages() {
  const api = useApi();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/me/messages").then((r) => setItems(r.data)).catch(() => {}); }, [api]);
  return items.map((m) => (
    <div key={m.id} className="mb-4 flex gap-2 rounded-lg border border-[#222d99]/30 bg-[#222d99]/5 p-3 text-sm text-slate-800" role="status" data-testid="in-app-message">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#222d99]" /><div><p className="font-semibold">{m.title}</p><p>{m.body}</p></div>
    </div>
  ));
}

function Breadcrumbs() {
  const { pathname } = useLocation();
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] !== "admin") return null;
  return (
    <nav aria-label="Breadcrumb" className="mb-4 text-sm text-slate-600" data-testid="breadcrumbs">
      <ol className="flex flex-wrap items-center gap-1">
        {parts.map((p, i) => {
          const to = "/" + parts.slice(0, i + 1).join("/");
          const label = CRUMBS[p] || "Details";
          if (p === "m") return null;
          return <li key={to} className="flex items-center gap-1">{i > 0 && <span aria-hidden="true">/</span>}{i < parts.length - 1 ? <Link to={to} className="rounded-sm hover:text-[#222d99] hover:underline">{label}</Link> : <span className="font-medium text-slate-900" aria-current="page">{label}</span>}</li>;
        })}
      </ol>
    </nav>
  );
}

function NavList({ role, onNavigate }) {
  const { me } = useMe();
  return (
    <nav className="flex min-h-0 flex-col gap-1 overflow-y-auto" data-testid="sidebar-nav">
      {NAV[role].filter((i) => allowed(i, me.user)).map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to} to={to} end={end} onClick={onNavigate}
          data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-[#222d99] text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}
        >
          <Icon className="h-4 w-4" /> {label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AppShell() {
  const { me } = useMe();
  const [open, setOpen] = useState(false);
  const role = me.user.is_admin ? "admin" : me.user.role;
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-slate-200 bg-white p-5 lg:flex">
        <Logo />
        <p className="font-mono-label mt-6 mb-3 text-[10px] uppercase tracking-[0.2em] text-slate-400" data-testid="sidebar-role-label">{me.user.is_admin ? me.user.role_name : role} workspace</p>
        <NavList role={role} />
        <div className="mt-auto border-t border-slate-100 pt-4">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#222d99] text-xs font-bold text-white" aria-hidden="true">{(me.user.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}</span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900" data-testid="sidebar-user-name">{me.user.name}</p>
              <p className="truncate text-xs text-slate-500">{me.user.email}</p>
            </div>
          </div>
          <LogoutButton className="mt-2 w-full" testid="sidebar-logout-btn" />
        </div>
      </aside>
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur-md lg:hidden">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="rounded-md p-2 hover:bg-slate-100" aria-label="Open menu" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 overflow-y-auto p-5">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <Logo />
            <div className="mt-6"><NavList role={role} onNavigate={() => setOpen(false)} /></div>
            <div className="mt-4 border-t border-slate-100 pt-3"><LogoutButton className="w-full" testid="mobile-sheet-logout-btn" /></div>
          </SheetContent>
        </Sheet>
        <Logo />
        <ProfileMenu testid="mobile-profile-menu" />
      </header>
      <header className="sticky top-0 z-30 hidden items-center justify-end border-b border-slate-200 bg-white/90 px-10 py-2 backdrop-blur-md lg:ml-64 lg:flex" data-testid="desktop-topbar">
        <ProfileMenu />
      </header>
      {me.user.is_admin && me.user.idle_minutes && <AdminIdleGuard minutes={me.user.idle_minutes} />}
      <main className="lg:pl-64">
        <div className="mx-auto max-w-6xl p-4 sm:p-6 lg:p-10"><Breadcrumbs /><Messages /><Outlet /></div>
      </main>
    </div>
  );
}

export const PageHeader = ({ eyebrow, title, children, testid }) => (
  <div className="rise mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div>
      {eyebrow && <p className="font-mono-label mb-2 text-xs uppercase tracking-[0.2em] text-[#222d99]">{eyebrow}</p>}
      <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl" data-testid={testid || "page-title"}>{title}</h1>
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);
